<template>
    <v-container fluid class="pa-4">
        <!-- Header -->
        <div class="d-flex flex-wrap align-center justify-space-between mb-4" style="gap:12px">
            <div>
                <h2 class="text-h5 font-weight-bold">Calendario</h2>
                <div class="text-body-2 text-medium-emphasis">
                    Manutenzioni, scadenze mezzi, sicurezza{{ isAdmin ? ' e pagamenti' : '' }} in un unico calendario
                </div>
            </div>
            <v-btn color="primary" prepend-icon="mdi-plus" @click="apriSceltaMacchina">
                Pianifica Manutenzione
            </v-btn>
        </div>

        <!-- Banner manutenzioni scadute -->
        <v-alert v-if="kpiScadute > 0" type="error" variant="tonal" class="mb-4 rounded-lg" density="comfortable">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap:8px">
                <span>
                    {{ kpiScadute }}
                    manutenzion{{ kpiScadute === 1 ? 'e scaduta richiede' : 'i scadute richiedono' }} attenzione
                </span>
                <v-btn size="small" color="error" variant="flat" @click="visualizzaScadute">Visualizza</v-btn>
            </div>
        </v-alert>

        <!-- Scadute ancora aperte: restano qui in OGNI mese e in ogni vista finché non vengono
             completate, a prescindere dal mese in cui sono scadute (il calendario mostra
             solo l'intervallo visualizzato, quindi altrimenti sparirebbero). -->
        <v-card v-if="scaduteVisibili.length" variant="outlined" color="error" class="mb-4 rounded-lg">
            <v-card-title class="d-flex align-center text-subtitle-1 cursor-pointer" style="cursor:pointer"
                @click="scaduteAperte = !scaduteAperte">
                <v-icon class="mr-2" color="error">mdi-alert-circle</v-icon>
                Scadute da completare ({{ scaduteVisibili.length }})
                <v-spacer />
                <v-icon>{{ scaduteAperte ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-card-title>
            <v-list v-if="scaduteAperte" density="compact" class="py-0" bg-color="transparent">
                <v-list-item v-for="ev in scaduteVisibili" :key="`${ev.categoria}-${ev.id}`"
                    :title="titoloEvento(ev)" :subtitle="`${etichettaCategoria(ev.categoria)} · scaduta il ${formatData(ev.data)}`"
                    @click="apriEvento(ev)">
                    <template #prepend>
                        <v-icon size="small" class="mr-2">{{ iconaCategoria(ev.categoria) }}</v-icon>
                    </template>
                    <template #append>
                        <v-btn size="small" :color="coloreBottone(ev)" variant="flat" @click.stop="apriEvento(ev)">
                            {{ etichettaBottone(ev) }}
                        </v-btn>
                    </template>
                </v-list-item>
            </v-list>
        </v-card>

        <!-- Barra vista + navigazione -->
        <div class="d-flex flex-wrap align-center justify-space-between mb-3" style="gap:8px">
            <v-btn-toggle :model-value="view" mandatory color="primary" density="comfortable" class="rounded-lg"
                @update:modelValue="cambiaVista">
                <v-btn value="agenda" variant="text">Agenda</v-btn>
                <v-btn value="settimana" variant="text">Settimana</v-btn>
                <v-btn value="mese" variant="text">Mese</v-btn>
            </v-btn-toggle>

            <div class="d-flex align-center">
                <template v-if="view !== 'agenda'">
                    <v-btn icon variant="text" size="small" @click="precedente">
                        <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                    <span class="mx-2 text-subtitle-2 font-weight-medium" style="min-width:160px; text-align:center">
                        {{ titoloRange }}
                    </span>
                    <v-btn icon variant="text" size="small" @click="successivo">
                        <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                </template>
                <v-btn variant="text" size="small" class="ml-2" @click="vaiOggi">Oggi</v-btn>
            </div>
        </div>

        <!-- Filtri: "Manutenzione" (macchinari/mezzi + bene specifico unico), "Stato"
             (solo scadute) e "Pagamenti" (solo Admin). Un bene specifico selezionato
             filtra SOLO la propria categoria (macchinari o mezzi): l'altra categoria
             e i pagamenti restano visibili secondo i propri interruttori, non vengono
             mai nascosti dalla scelta di un bene specifico. -->
        <v-row class="mb-2" dense>
            <v-col cols="12" :md="isAdmin ? 6 : 8">
                <v-card variant="outlined" class="rounded-lg pa-3">
                    <div class="text-caption text-medium-emphasis font-weight-medium mb-2">Manutenzione</div>
                    <v-row dense align="center">
                        <v-col cols="6" sm="3">
                            <v-checkbox v-model="includiManutenzioni" label="Macchinari" density="compact"
                                hide-details />
                        </v-col>
                        <v-col cols="6" sm="3">
                            <v-checkbox v-model="includiMezzi" label="Mezzi" density="compact" hide-details />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select v-model="filtroBeneSpecifico" :items="beniDisponibili" item-title="nome"
                                item-value="chiave" label="Bene specifico (macchina o mezzo)" density="compact"
                                variant="outlined" hide-details clearable />
                        </v-col>
                    </v-row>
                    <v-row dense class="mt-1">
                        <v-col cols="12">
                            <v-select v-model="filtroTipiManutenzioneIds" :items="tipiManutenzione" item-title="nome"
                                item-value="id" label="Tutti i tipi di manutenzione" density="compact"
                                variant="outlined" hide-details multiple chips closable-chips clearable />
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>

            <v-col cols="12" md="2">
                <v-card variant="outlined" class="rounded-lg pa-3 h-100 d-flex flex-column justify-center">
                    <div class="text-caption text-medium-emphasis font-weight-medium mb-2">Stato</div>
                    <v-checkbox v-model="soloScadute" label="Solo scadute" density="compact" color="error"
                        hide-details />
                    <v-checkbox v-model="includiSicurezza" label="Sicurezza" density="compact" hide-details
                        class="mt-1" />
                </v-card>
            </v-col>

            <!-- Blocco Pagamenti: visibile solo per ruolo Admin, stesso meccanismo di
                 user/getRole già usato altrove (es. Macchine.vue) per il RBAC lato UI. -->
            <v-col cols="12" md="4" v-if="isAdmin">
                <v-card variant="outlined" class="rounded-lg pa-3">
                    <div class="text-caption text-medium-emphasis font-weight-medium mb-2">Pagamenti</div>
                    <v-select v-model="filtroTipiPagamentoIds" :items="tipiPagamento" item-title="nome"
                        item-value="id" label="Tutte le tipologie" density="compact" variant="outlined" hide-details
                        multiple chips closable-chips clearable />
                </v-card>
            </v-col>
        </v-row>

        <!-- ===================== AGENDA ===================== -->
        <v-card v-if="view === 'agenda'" variant="outlined" class="rounded-lg pa-2">
            <div class="px-2 pt-2 pb-1 text-subtitle-1 font-weight-medium">
                Prossimi eventi
                <span class="text-medium-emphasis text-body-2">({{ eventiAgenda.length }} totali)</span>
            </div>

            <div v-if="gruppiAgenda.length === 0" class="text-center text-medium-emphasis pa-8">
                Nessun evento trovato.
            </div>

            <div v-for="gruppo in gruppiAgenda" :key="gruppo.iso" class="mb-3"
                :class="gruppo.oggi ? 'gruppo-oggi' : ''">
                <div class="px-2 py-1 text-caption font-weight-medium"
                    :class="gruppo.oggi ? 'text-primary' : 'text-medium-emphasis'">
                    {{ gruppo.label }}{{ gruppo.oggi ? ' · Oggi' : '' }}
                    · {{ gruppo.eventi.length }} event{{ gruppo.eventi.length === 1 ? 'o' : 'i' }}
                </div>

                <v-card v-for="ev in gruppo.eventi" :key="ev.id" variant="outlined" class="mb-2 rounded-lg"
                    :class="ev.colore === 'red' ? 'evento-scaduto' : ''">
                    <v-card-text class="d-flex align-center justify-space-between flex-wrap" style="gap:8px">
                        <div>
                            <v-chip size="small" :color="ev.colore" variant="flat" class="mb-1">
                                {{ etichettaStato(ev.colore) }}
                            </v-chip>
                            <div class="font-weight-medium">{{ titoloEvento(ev) }}</div>
                            <div class="text-caption text-medium-emphasis d-flex align-center mt-1">
                                <v-icon size="x-small" class="mr-1">{{ iconaCategoria(ev.categoria) }}</v-icon>
                                {{ etichettaCategoria(ev.categoria) }}
                            </div>
                        </div>
                        <v-btn size="small" :color="coloreBottone(ev)" :variant="ev.colore === 'blue' ? 'text' : 'flat'"
                            @click="apriEvento(ev)">
                            {{ etichettaBottone(ev) }}
                        </v-btn>
                    </v-card-text>
                </v-card>
            </div>
        </v-card>

        <!-- ===================== SETTIMANA ===================== -->
        <v-card v-else-if="view === 'settimana'" variant="outlined" class="rounded-lg pa-2">
            <v-row dense>
                <v-col v-for="g in giorniSettimanaGrid" :key="g.iso" cols>
                    <div class="text-center text-caption font-weight-bold pb-1" :class="g.oggi ? 'text-primary' : ''">
                        {{ g.nomeGiorno }}<br><span class="text-body-2">{{ g.numero }}</span>
                    </div>
                    <v-sheet :color="g.oggi ? 'blue-lighten-5' : undefined" class="rounded-lg pa-1" min-height="220"
                        border>
                        <div v-if="g.eventi.length === 0" class="text-caption text-medium-emphasis text-center mt-2">
                            Nessuno
                        </div>
                        <div v-for="ev in g.eventi" :key="ev.id" class="d-flex align-center mb-1 evento-mini"
                            style="cursor:pointer" @click="apriEvento(ev)">
                            <v-icon :color="ev.colore" size="8" class="mr-1">mdi-circle</v-icon>
                            <v-icon size="10" class="mr-1">{{ iconaCategoria(ev.categoria) }}</v-icon>
                            <span class="text-caption text-truncate">{{ titoloEvento(ev) }}</span>
                        </div>
                    </v-sheet>
                </v-col>
            </v-row>
            <div class="d-flex mt-3 px-2 flex-wrap" style="gap:16px">
                <span class="text-caption"><v-icon color="green" size="10">mdi-circle</v-icon> Pianificata</span>
                <span class="text-caption"><v-icon color="amber" size="10">mdi-circle</v-icon> Prevista oggi</span>
                <span class="text-caption"><v-icon color="red" size="10">mdi-circle</v-icon> Scaduta</span>
                <span class="text-caption"><v-icon color="orange" size="10">mdi-circle</v-icon> In corso</span>
                <span class="text-caption"><v-icon color="blue" size="10">mdi-circle</v-icon> Completata</span>
                <span class="text-caption"><v-icon size="10">mdi-cog</v-icon> Macchinario</span>
                <span class="text-caption"><v-icon size="10">mdi-truck</v-icon> Mezzo</span>
                <span class="text-caption"><v-icon size="10">mdi-shield-check</v-icon> Sicurezza</span>
                <span v-if="isAdmin" class="text-caption"><v-icon size="10">mdi-cash</v-icon> Pagamento</span>
            </div>
        </v-card>

        <!-- ===================== MESE ===================== -->
        <v-card v-else variant="outlined" class="rounded-lg pa-2">
            <v-row class="mb-1" dense>
                <v-col v-for="g in giorniSettimana" :key="g" cols class="text-center text-caption font-weight-bold">
                    {{ g }}
                </v-col>
            </v-row>
            <v-row v-for="(settimana, i) in griglieMese" :key="i" dense class="giorno-mese-row">
                <v-col v-for="giorno in settimana" :key="giorno.iso" class="pa-1 giorno-mese-col">
                    <v-sheet :class="['rounded-lg pa-1 giorno-mese-sheet', !giorno.delMese ? 'bg-grey-lighten-4' : '']"
                        border>
                        <div class="text-caption"
                            :class="giorno.oggi ? 'font-weight-bold text-primary' : 'text-medium-emphasis'">
                            {{ giorno.numero }}
                        </div>
                        <div v-for="ev in giorno.eventi.slice(0, 3)" :key="ev.id" class="mb-1">
                            <v-chip size="x-small" :color="ev.colore" variant="flat" class="evento-chip-mese"
                                :prepend-icon="iconaCategoria(ev.categoria)" :title="titoloEvento(ev)"
                                @click="apriEvento(ev)">
                                <span class="text-truncate">{{ titoloEvento(ev) }}</span>
                            </v-chip>
                        </div>
                        <div v-if="giorno.eventi.length > 3" class="text-caption text-medium-emphasis">
                            +{{ giorno.eventi.length - 3 }} altr{{ giorno.eventi.length - 3 === 1 ? 'o' : 'i' }}
                        </div>
                    </v-sheet>
                </v-col>
            </v-row>
            <div class="d-flex mt-3 px-2 flex-wrap" style="gap:16px">
                <span class="text-caption"><v-icon color="green" size="10">mdi-circle</v-icon> Pianificata</span>
                <span class="text-caption"><v-icon color="amber" size="10">mdi-circle</v-icon> Prevista oggi</span>
                <span class="text-caption"><v-icon color="red" size="10">mdi-circle</v-icon> Scaduta</span>
                <span class="text-caption"><v-icon color="orange" size="10">mdi-circle</v-icon> In corso</span>
                <span class="text-caption"><v-icon color="blue" size="10">mdi-circle</v-icon> Completata</span>
                <span class="text-caption"><v-icon size="10">mdi-cog</v-icon> Macchinario</span>
                <span class="text-caption"><v-icon size="10">mdi-truck</v-icon> Mezzo</span>
                <span class="text-caption"><v-icon size="10">mdi-shield-check</v-icon> Sicurezza</span>
                <span v-if="isAdmin" class="text-caption"><v-icon size="10">mdi-cash</v-icon> Pagamento</span>
            </div>
        </v-card>

        <!-- Dialog: scelta macchina (step 1 della pianificazione) -->
        <seleziona-macchina-dialog v-model="dialogSceltaMacchina" @seleziona="onMacchinaSelezionata" />

        <!-- Dialog: nuova manutenzione (step 2, componente esistente) -->
        <dialog-nuova-manutenzione v-if="macchinaPerNuova" v-model="dialogNuovaManutenzione"
            :macchina-id="macchinaPerNuova" @salvato="onManutenzionePianificata" />

        <!-- Dialog: completamento/dettaglio Manutenzione (eventi categoria "Manutenzione") -->
        <dialog-completa-manutenzione v-if="eventoSelezionato" v-model="dialogCompletamento"
            :manutenzione-id="eventoSelezionato.id" :macchina-id="eventoSelezionato.entitaId"
            @completata="onManutenzioneCompletata" />
        <!-- Per le manutenzioni con fermo macchina non ancora iniziate, questo stesso
             dialog mostra un riepilogo con pulsante "Avvia manutenzione" invece di
             avviarla subito al click (vedi DialogDettaglioManutenzione.vue). -->
        <dialog-dettaglio-manutenzione v-if="eventoSelezionato" v-model="dialogDettaglio"
            :manutenzione-id="eventoSelezionato.id" @iniziata="onManutenzioneIniziata" />

        <!-- Dialog: completamento/dettaglio Scadenza mezzo (eventi categoria "Mezzo") -->
        <dialog-completa-scadenza-mezzo v-if="eventoSelezionato" v-model="dialogCompletamentoMezzo"
            :scadenza-mezzo-id="eventoSelezionato.id" :mezzo-id="eventoSelezionato.entitaId"
            @completata="onScadenzaMezzoCompletata" />
        <dialog-dettaglio-scadenza-mezzo v-if="eventoSelezionato" v-model="dialogDettaglioMezzo"
            :scadenza-mezzo-id="eventoSelezionato.id" />

        <!-- Dialog: dettaglio Pagamento già pagato (sola lettura). Per i pagamenti
             ancora da pagare si apre invece dialog-completa-pagamento qui sotto. -->
        <dialog-pagamento v-if="pagamentoPerDettaglio" v-model="dialogDettaglioPagamento"
            :pagamento="pagamentoPerDettaglio" sola-lettura />

        <!-- Dialog: segna come pagato, per i pagamenti non ancora saldati aperti dal
             calendario (prima si apriva solo il dettaglio in sola lettura, senza
             possibilità di completare il pagamento). -->
        <dialog-completa-pagamento v-if="pagamentoPerDettaglio" v-model="dialogCompletaPagamento"
            :pagamento="pagamentoPerDettaglio" @completato="onPagamentoCompletato" />

        <!-- Dialog: sostituzione contenuto sicurezza (eventi categoria "Sicurezza"
             non ancora sostituiti): stesso dialog/azione della pagina "Sicurezza",
             aperto direttamente dal calendario senza perdere il riferimento. Per i
             contenuti già sostituiti (evento "blue", storico) si apre invece la
             scheda del dispositivo, vedi apriEvento(). -->
        <dialog-sostituisci-contenuto v-if="eventoSelezionato" v-model="dialogSostituisciSicurezza"
            :contenuto-sicurezza-id="eventoSelezionato.id" @sostituito="onContenutoSicurezzaSostituito" />
    </v-container>
</template>

<script>
import { get_eventi_calendario, get_eventi_scaduti } from "@/api/api.calendario.js";
import { get_kpi } from "@/api/api.dashboard.js";
import DialogNuovaManutenzione from "@/components/manutenzione/DialogNuovaManutenzione.vue";
import DialogCompletaManutenzione from "@/components/manutenzione/DialogCompletaManutenzione.vue";
import DialogDettaglioManutenzione from "@/components/manutenzione/DialogDettaglioManutenzione.vue";
import SelezionaMacchinaDialog from "@/components/manutenzione/SelezionaMacchinaDialog.vue";
import DialogCompletaScadenzaMezzo from "@/components/scadenzeMezzo/DialogCompletaScadenzaMezzo.vue";
import DialogDettaglioScadenzaMezzo from "@/components/scadenzeMezzo/DialogDettaglioScadenzaMezzo.vue";
import DialogPagamento from "@/components/pagamenti/DialogPagamento.vue";
import DialogSostituisciContenuto from "@/components/sicurezza/DialogSostituisciContenuto.vue";
import DialogCompletaPagamento from "@/components/pagamenti/DialogCompletaPagamento.vue";
import { iconaCategoria, etichettaCategoria } from "@/constants/categoriaEvento.js";
import { leggiFiltriDaQuery, scriviFiltriSuQuery } from "@/utils/filtriUrl.js";

export default {
    name: "CalendarioPage",

    components: {
        DialogNuovaManutenzione,
        DialogCompletaManutenzione,
        DialogDettaglioManutenzione,
        SelezionaMacchinaDialog,
        DialogCompletaScadenzaMezzo,
        DialogDettaglioScadenzaMezzo,
        DialogPagamento,
        DialogCompletaPagamento,
        DialogSostituisciContenuto,
    },

    data() {
        return {
            view: "agenda", // 'agenda' | 'settimana' | 'mese'
            refDate: new Date(),

            eventiRaw: [],
            // Tutte le voci aperte già scadute (qualunque mese): vedi pannello "Scadute da completare".
            scaduteRaw: [],
            scaduteAperte: true,
            kpiScadute: 0,

            // Blocco filtri "Manutenzione": Macchinari/Mezzi inclusi + entità specifica.
            includiManutenzioni: true,
            includiMezzi: true,
            filtroMacchinaId: null,
            filtroMezzoId: null,
            filtroTipiManutenzioneIds: [],

            // Blocco filtri "Stato": si applica a tutte le categorie.
            soloScadute: false,
            includiSicurezza: true,

            // Blocco filtri "Pagamenti" (solo Admin): tipologie selezionate (id).
            filtroTipiPagamentoIds: [],

            giorniSettimana: ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"],

            dialogSceltaMacchina: false,
            macchinaPerNuova: null,
            dialogNuovaManutenzione: false,

            eventoSelezionato: null,
            dialogCompletamento: false,
            dialogDettaglio: false,
            dialogCompletamentoMezzo: false,
            dialogDettaglioMezzo: false,

            dialogDettaglioPagamento: false,
            dialogCompletaPagamento: false,
            pagamentoPerDettaglio: null,

            dialogSostituisciSicurezza: false,
        };
    },

    computed: {
        macchine() {
            return this.$store.getters["macchine/lista"];
        },
        mezzi() {
            return this.$store.getters["mezzi/lista"];
        },
        tipiPagamento() {
            return this.$store.getters["tipiPagamento/lista"];
        },
        tipiManutenzione() {
            return this.$store.getters["tipiManutenzione/lista"];
        },
        ruolo() {
            return this.$store.getters["user/getRole"];
        },
        // Il blocco filtri "Pagamenti" va mostrato solo per ruolo Admin: stesso
        // meccanismo (user/getRole) già usato nelle altre viste (es. Macchine.vue)
        // per il controllo RBAC lato UI. Gli eventi Categoria=="Pagamento" comunque
        // non arrivano affatto dal server per un utente non Admin (filtro lato
        // server, vedi Fase 4 del piano), quindi qui è solo questione di UI.
        isAdmin() {
            return this.ruolo === "Admin";
        },

        // Nomi delle tipologie di pagamento selezionate: il filtro si applica
        // confrontando con "sottotipo" (unico campo garantito dal contratto per
        // identificare la tipologia di un evento Pagamento, non essendoci un
        // TipoPagamentoId esplicito nell'EventoCalendarioDto).
        filtroTipiPagamentoNomi() {
            if (!this.filtroTipiPagamentoIds.length) return [];
            return this.filtroTipiPagamentoIds
                .map(id => (this.tipiPagamento.find(t => t.id === id) || {}).nome)
                .filter(Boolean);
        },

        // Stesso meccanismo di filtroTipiPagamentoNomi: l'EventoCalendarioDto non ha
        // un TipoManutenzioneId esplicito, solo "sottotipo" (il nome), quindi il
        // filtro confronta per nome anziché per id.
        filtroTipiManutenzioneNomi() {
            if (!this.filtroTipiManutenzioneIds.length) return [];
            return this.filtroTipiManutenzioneIds
                .map(id => (this.tipiManutenzione.find(t => t.id === id) || {}).nome)
                .filter(Boolean);
        },

        // Aggrega tutti i filtri sincronizzati con l'URL: essendo il filtraggio qui
        // client-side (nessun carica() per-filtro), la scrittura sull'URL avviene
        // tramite un watcher deep su questo computed (vedi sotto), non agganciata a carica().
        filtriCalendarioUrl() {
            return {
                vista: this.view,
                tipoManutenzioneIds: this.filtroTipiManutenzioneIds,
                macchinaId: this.filtroMacchinaId,
                mezzoId: this.filtroMezzoId,
                soloScadute: this.soloScadute,
                tipiPagamentoIds: this.filtroTipiPagamentoIds,
            };
        },

        // Un'unica select "Bene specifico" al posto delle due separate (macchina/
        // mezzo): scegliere un bene filtra solo la sua categoria, l'altra e i
        // pagamenti restano visibili secondo i propri interruttori — selezionare
        // una macchina non nasconde mai i mezzi (e viceversa).
        beniDisponibili() {
            return [
                ...this.macchine.map(m => ({ chiave: `macchina:${m.id}`, nome: `${m.nome} (macchinario)` })),
                ...this.mezzi.map(m => ({ chiave: `mezzo:${m.id}`, nome: `${m.nome} (mezzo)` })),
            ];
        },

        filtroBeneSpecifico: {
            get() {
                if (this.filtroMacchinaId) return `macchina:${this.filtroMacchinaId}`;
                if (this.filtroMezzoId) return `mezzo:${this.filtroMezzoId}`;
                return null;
            },
            set(valore) {
                this.filtroMacchinaId = null;
                this.filtroMezzoId = null;
                if (!valore) return;
                const [tipo, id] = valore.split(":");
                if (tipo === "macchina") this.filtroMacchinaId = Number(id);
                else if (tipo === "mezzo") this.filtroMezzoId = Number(id);
            },
        },

        // Filtro lato client: i dati vengono scaricati per l'intervallo della vista corrente,
        // poi filtrati per categoria/entità/tipologia senza richiedere una nuova chiamata al backend.
        eventiFiltrati() {
            return this.eventiRaw.filter(e => this.passaFiltri(e));
        },

        // Elenco "Scadute da completare": stessi filtri di categoria/bene/tipologia del
        // calendario. "Solo scadute" qui e irrilevante (sono gia tutte scadute).
        scaduteVisibili() {
            return this.scaduteRaw.filter(e => this.passaFiltri(e, true));
        },

        // Solo per la vista Agenda: un evento passato ha senso mostrarlo lì solo se
        // ancora "aperto" (non completato/pagato) — il passato-e-completato appartiene
        // solo allo Storico, altrimenti l'agenda si riempie di eventi ormai chiusi.
        // Settimana/Mese non applicano questa esclusione: mostrano semplicemente cosa
        // è successo in un dato giorno, passato incluso.
        eventiAgenda() {
            const oggiIso = this.toIso(new Date());
            return this.eventiFiltrati.filter(e => {
                if (!e.data) return true;
                const iso = e.data.substr(0, 10);
                return iso >= oggiIso || e.colore !== "blue";
            });
        },

        inizioSettimana() {
            const d = new Date(this.refDate);
            const offset = (d.getDay() + 6) % 7; // Lunedì come primo giorno
            d.setDate(d.getDate() - offset);
            d.setHours(0, 0, 0, 0);
            return d;
        },

        giorniSettimanaGrid() {
            const nomiBrevi = ["LUN", "MAR", "MER", "GIO", "VEN", "SAB", "DOM"];
            const oggiIso = this.toIso(new Date());
            const giorni = [];
            for (let i = 0; i < 7; i++) {
                const d = new Date(this.inizioSettimana);
                d.setDate(d.getDate() + i);
                const iso = this.toIso(d);
                giorni.push({
                    iso,
                    numero: d.getDate(),
                    nomeGiorno: nomiBrevi[i],
                    oggi: iso === oggiIso,
                    eventi: this.eventiFiltrati.filter(e => e.data && e.data.substr(0, 10) === iso),
                });
            }
            return giorni;
        },

        griglieMese() {
            const anno = this.refDate.getFullYear();
            const mese = this.refDate.getMonth();
            const primoDelMese = new Date(anno, mese, 1);
            const offset = (primoDelMese.getDay() + 6) % 7;
            const inizioGriglia = new Date(anno, mese, 1 - offset);

            const oggiIso = this.toIso(new Date());
            const settimane = [];
            let cursore = new Date(inizioGriglia);

            for (let s = 0; s < 6; s++) {
                const settimana = [];
                for (let g = 0; g < 7; g++) {
                    const iso = this.toIso(cursore);
                    settimana.push({
                        iso,
                        numero: cursore.getDate(),
                        delMese: cursore.getMonth() === mese,
                        oggi: iso === oggiIso,
                        eventi: this.eventiFiltrati.filter(e => e.data && e.data.substr(0, 10) === iso),
                    });
                    cursore.setDate(cursore.getDate() + 1);
                }
                settimane.push(settimana);
            }
            return settimane;
        },

        gruppiAgenda() {
            const mappa = {};
            this.eventiAgenda.forEach(e => {
                if (!e.data) return;
                const iso = e.data.substr(0, 10);
                if (!mappa[iso]) mappa[iso] = [];
                mappa[iso].push(e);
            });

            const oggiIso = this.toIso(new Date());
            return Object.keys(mappa).sort().map(iso => ({
                iso,
                oggi: iso === oggiIso,
                label: this.capitalize(
                    new Date(iso + "T00:00:00").toLocaleDateString("it-IT", {
                        day: "numeric", month: "long", year: "numeric",
                    })
                ),
                eventi: mappa[iso],
            }));
        },

        titoloRange() {
            if (this.view === "settimana") {
                const fine = new Date(this.inizioSettimana);
                fine.setDate(fine.getDate() + 6);
                const opts = { day: "numeric", month: "short" };
                const ini = this.capitalize(this.inizioSettimana.toLocaleDateString("it-IT", opts));
                const fin = this.capitalize(fine.toLocaleDateString("it-IT", opts));
                return `${ini} - ${fin} ${fine.getFullYear()}`;
            }
            if (this.view === "mese") {
                return this.capitalize(
                    this.refDate.toLocaleDateString("it-IT", { month: "long", year: "numeric" })
                );
            }
            return "";
        },
    },

    watch: {
        // Filtraggio client-side (vedi eventiFiltrati): a differenza delle altre
        // pagine, qui non c'è un carica() per-filtro da cui agganciare la scrittura
        // sull'URL, quindi si osserva l'intero blocco filtri in un colpo.
        filtriCalendarioUrl: {
            deep: true,
            handler(filtri) {
                scriviFiltriSuQuery(this.$router, this.$route, filtri);
            },
        },
    },

    mounted() {
        // Le chiavi nell'URL (vedi filtriCalendarioUrl) non coincidono 1:1 con i nomi
        // dei data() reali (es. "macchinaId" -> filtroMacchinaId): mapping esplicito,
        // assegnando solo le chiavi effettivamente presenti nell'URL (leggiFiltriDaQuery
        // omette quelle assenti, lasciando intatti i default sotto).
        const filtriUrl = leggiFiltriDaQuery(this.$route, {
            vista: "string",
            tipoManutenzioneIds: "number[]",
            macchinaId: "number",
            mezzoId: "number",
            soloScadute: "boolean",
            tipiPagamentoIds: "number[]",
        });
        if (filtriUrl.vista !== undefined) this.view = filtriUrl.vista;
        if (filtriUrl.tipoManutenzioneIds !== undefined) this.filtroTipiManutenzioneIds = filtriUrl.tipoManutenzioneIds;
        if (filtriUrl.macchinaId !== undefined) this.filtroMacchinaId = filtriUrl.macchinaId;
        if (filtriUrl.mezzoId !== undefined) this.filtroMezzoId = filtriUrl.mezzoId;
        if (filtriUrl.soloScadute !== undefined) this.soloScadute = filtriUrl.soloScadute;
        if (filtriUrl.tipiPagamentoIds !== undefined) this.filtroTipiPagamentoIds = filtriUrl.tipiPagamentoIds;

        this.$store.dispatch("macchine/carica");
        this.$store.dispatch("mezzi/carica");
        this.$store.dispatch("tipiManutenzione/carica");
        if (this.isAdmin) this.$store.dispatch("tipiPagamento/carica");
        this.caricaKpi();
        this.caricaScadute();
        this.carica();
    },

    methods: {
        iconaCategoria,
        etichettaCategoria,

        // Filtri lato client (categoria/bene/tipologia). `ignoraSoloScadute`: usato dalla
        // lista delle scadute, dove il filtro "Solo scadute" non ha senso.
        passaFiltri(e, ignoraSoloScadute = false) {
            if (!ignoraSoloScadute && this.soloScadute && e.colore !== "red") return false;

            if (e.categoria === "Manutenzione") {
                if (!this.includiManutenzioni) return false;
                if (this.filtroMacchinaId && e.entitaId !== this.filtroMacchinaId) return false;
                if (this.filtroTipiManutenzioneNomi.length && !this.filtroTipiManutenzioneNomi.includes(e.sottotipo)) {
                    return false;
                }
                return true;
            }
            if (e.categoria === "Mezzo") {
                if (!this.includiMezzi) return false;
                if (this.filtroMezzoId && e.entitaId !== this.filtroMezzoId) return false;
                return true;
            }
            if (e.categoria === "Pagamento") {
                if (this.filtroTipiPagamentoNomi.length && !this.filtroTipiPagamentoNomi.includes(e.sottotipo)) {
                    return false;
                }
                return true;
            }
            if (e.categoria === "Sicurezza") {
                if (!this.includiSicurezza) return false;
                return true;
            }
            return true;
        },

        formatData(data) {
            return data ? new Date(data).toLocaleDateString("it-IT") : "";
        },

        toIso(data) {
            return data.toISOString().substr(0, 10);
        },

        addDays(data, n) {
            const d = new Date(data);
            d.setDate(d.getDate() + n);
            return d;
        },

        capitalize(str) {
            return str ? str.charAt(0).toUpperCase() + str.slice(1) : str;
        },

        etichettaStato(colore) {
            return { green: "Pianificata", amber: "Prevista oggi", red: "Scaduta", orange: "In corso", blue: "Completata" }[colore] || colore;
        },

        // Il nuovo EventoCalendarioDto non porta più un campo "Titolo" precalcolato
        // (aveva senso solo per Manutenzione): lo componiamo qui da Sottotipo
        // (nome della tipologia) + EntitaNome (macchina/mezzo, o fornitore/
        // beneficiario per i pagamenti).
        titoloEvento(ev) {
            if (ev.categoria === "Pagamento") {
                return ev.entitaNome ? `${ev.sottotipo} — ${ev.entitaNome}` : ev.sottotipo;
            }
            return ev.entitaNome ? `${ev.entitaNome} — ${ev.sottotipo}` : ev.sottotipo;
        },

        etichettaBottone(ev) {
            if (ev.categoria === "Sicurezza") return ev.colore === "blue" ? "Apri dispositivo" : "Sostituisci";
            if (ev.colore === "blue") return "Dettagli";
            if (ev.categoria === "Pagamento") return "Segna pagato";
            if (ev.colore === "orange") return "Completa";
            if (ev.categoria === "Manutenzione") return ev.richiedeFermoMacchina ? "Inizia" : "Completa";
            return "Completa"; // Mezzo: mai "Inizia", niente wizard di fermo.
        },

        coloreBottone(ev) {
            if (ev.categoria === "Sicurezza") return ev.colore === "blue" ? "primary" : "success";
            if (ev.colore === "blue" || ev.colore === "orange") return "primary";
            if (ev.categoria === "Manutenzione" && ev.richiedeFermoMacchina) return "orange-darken-2";
            return "success";
        },

        // Scarica gli eventi per l'intervallo di date rilevante alla vista corrente.
        // I filtri di categoria/entità/tipologia restano client-side (eventiFiltrati):
        // qui si passa solo il range di date, mai i filtri, per non generare un
        // nuovo fetch a ogni cambio filtro (comportamento invariato rispetto a prima).
        carica() {
            const oggi = new Date();
            let dataInizio, dataFine;

            if (this.view === "agenda") {
                dataInizio = this.addDays(oggi, -90);
                dataFine = this.addDays(oggi, 180);
            } else if (this.view === "settimana") {
                dataInizio = this.inizioSettimana;
                dataFine = this.addDays(this.inizioSettimana, 6);
            } else {
                const anno = this.refDate.getFullYear();
                const mese = this.refDate.getMonth();
                dataInizio = new Date(anno, mese, -6);
                dataFine = new Date(anno, mese + 1, 6);
            }

            // Le scadute si riscaricano a ogni caricamento: dopo un completamento/pagamento/
            // sostituzione l'elemento deve sparire anche dal pannello "Scadute da completare".
            this.caricaScadute();

            return get_eventi_calendario(this.toIso(dataInizio), this.toIso(dataFine))
                .then(res => { this.eventiRaw = res.data; })
                .catch(err => console.log(err));
        },

        caricaScadute() {
            return get_eventi_scaduti()
                .then(res => { this.scaduteRaw = res.data; })
                .catch(err => console.log(err));
        },

        caricaKpi() {
            get_kpi()
                .then(res => { this.kpiScadute = (res.data && res.data.manutenzioniScadute) || 0; })
                .catch(err => console.log(err));
        },

        cambiaVista(v) {
            this.view = v;
            this.refDate = new Date();
            this.carica();
        },

        precedente() {
            if (this.view === "settimana") this.refDate = this.addDays(this.refDate, -7);
            else if (this.view === "mese")
                this.refDate = new Date(this.refDate.getFullYear(), this.refDate.getMonth() - 1, 1);
            this.carica();
        },

        successivo() {
            if (this.view === "settimana") this.refDate = this.addDays(this.refDate, 7);
            else if (this.view === "mese")
                this.refDate = new Date(this.refDate.getFullYear(), this.refDate.getMonth() + 1, 1);
            this.carica();
        },

        vaiOggi() {
            this.refDate = new Date();
            if (this.view === "agenda") {
                this.carica().then(() => {
                    this.$nextTick(() => {
                        const el = this.$el.querySelector(".gruppo-oggi");
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    });
                });
            } else {
                this.carica();
            }
        },

        // Passa in Agenda e scrolla fino alla prima manutenzione scaduta.
        visualizzaScadute() {
            this.view = "agenda";
            this.refDate = new Date();
            this.carica().then(() => {
                this.$nextTick(() => {
                    setTimeout(() => {
                        const el = this.$el.querySelector(".evento-scaduto");
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                    }, 50);
                });
            });
        },

        // Instrada al dialog corretto in base alla categoria dell'evento.
        apriEvento(ev) {
            if (ev.categoria === "Manutenzione") {
                this.eventoSelezionato = ev;
                this.$nextTick(() => {
                    // Manutenzioni con fermo macchina non ancora avviate: mostra il
                    // dettaglio con conferma esplicita invece di avviarle subito al
                    // click (comportamento precedente, senza chiedere conferma).
                    if (ev.colore === "blue" || (ev.richiedeFermoMacchina && ev.colore !== "orange")) {
                        this.dialogDettaglio = true;
                    } else {
                        this.dialogCompletamento = true;
                    }
                });
                return;
            }

            if (ev.categoria === "Mezzo") {
                this.eventoSelezionato = ev;
                this.$nextTick(() => {
                    if (ev.colore === "blue") this.dialogDettaglioMezzo = true;
                    else this.dialogCompletamentoMezzo = true;
                });
                return;
            }

            if (ev.categoria === "Pagamento") {
                this.apriDettaglioPagamento(ev);
                return;
            }

            // Sicurezza: un contenuto non ancora sostituito si sostituisce
            // direttamente dal calendario (stesso dialog/azione della pagina
            // "Sicurezza"), senza perdere il riferimento al calendario. Un evento
            // già sostituito (storico, "blue") non ha più nulla da sostituire: si
            // va alla scheda del dispositivo, dove si trova anche lo storico.
            if (ev.categoria === "Sicurezza") {
                if (ev.colore === "blue") {
                    this.$router.push(`/sicurezza/${ev.entitaId}`);
                    return;
                }
                this.eventoSelezionato = ev;
                this.$nextTick(() => {
                    this.dialogSostituisciSicurezza = true;
                });
            }
        },

        onManutenzioneCompletata() {
            this.dialogCompletamento = false;
            this.carica();
            this.caricaKpi();
        },

        onManutenzioneIniziata() {
            this.dialogDettaglio = false;
            this.carica();
            this.caricaKpi();
        },

        onScadenzaMezzoCompletata() {
            this.dialogCompletamentoMezzo = false;
            this.carica();
        },

        // Pagamento già pagato: dettaglio in sola lettura. Non ancora pagato: apre
        // direttamente il completamento (prima si apriva sempre e solo il dettaglio
        // in sola lettura, senza modo di segnarlo pagato dal calendario).
        async apriDettaglioPagamento(ev) {
            try {
                await this.$store.dispatch("pagamenti/caricaDettaglio", ev.id);
                this.pagamentoPerDettaglio = this.$store.getters["pagamenti/corrente"];
                this.$nextTick(() => {
                    if (ev.colore === "blue") this.dialogDettaglioPagamento = true;
                    else this.dialogCompletaPagamento = true;
                });
            } catch (error) {
                console.log(error);
            }
        },

        onPagamentoCompletato() {
            this.dialogCompletaPagamento = false;
            this.carica();
        },

        onContenutoSicurezzaSostituito() {
            this.dialogSostituisciSicurezza = false;
            this.carica();
        },

        apriSceltaMacchina() {
            this.macchinaPerNuova = null;
            this.dialogSceltaMacchina = true;
        },

        onMacchinaSelezionata(macchinaId) {
            this.macchinaPerNuova = macchinaId;
            this.$nextTick(() => {
                this.dialogNuovaManutenzione = true;
            });
        },

        onManutenzionePianificata() {
            this.dialogNuovaManutenzione = false;
            this.macchinaPerNuova = null;
            this.carica();
            this.caricaKpi();
        },
    },
};
</script>

<style scoped>
.evento-mini {
    overflow: hidden;
}

.gruppo-oggi {
    scroll-margin-top: 12px;
}

.giorno-mese-row {
    flex-wrap: nowrap;
}

.giorno-mese-col {
    flex: 1 1 0;
    min-width: 0;
    max-width: calc(100% / 7);
}

.giorno-mese-sheet {
    width: 100%;
    height: 100px;
    overflow: hidden;
}

.evento-chip-mese {
    max-width: 100%;
    cursor: pointer;
}

.evento-chip-mese :deep(.v-chip__content) {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
